'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Form from '@radix-ui/react-form';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { CollectionDefinition } from 'postman-collection';
import CopyButton from '@components/_components/ui/code-generator/CopyButton';
import { useExplorer } from '@/hooks/useExplorer';
import { snakeCase } from 'change-case-all';

interface ExportDescriptionCardProps {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  generatePostmanCollection: (
    codeSampleDescription?: string
  ) => (string | CollectionDefinition)[] | null;
}

const ExportDescriptionCard: React.FC<ExportDescriptionCardProps> = ({
  isVisible,
  setIsVisible,
  generatePostmanCollection,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [codeSample, setCodeSample] = useState<string>('');
  const [exportDescription, setExportDescription] = useState<string>('');
  const [githubUrl, setGithubUrl] = useState<string>('');
  const { api, endpoint } = useExplorer();

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isVisible]);

  useEffect(() => {
    if (exportDescription) {
      const generatedPostmanCollection =
        generatePostmanCollection(exportDescription);
      if (generatedPostmanCollection?.length == 2 && endpoint) {
        const postmanCollection = generatedPostmanCollection[0];
        const fileName = generatedPostmanCollection[1] as string;
        setCodeSample(JSON.stringify(postmanCollection, null, 2));
        setGithubUrl(
          `https://github.com/chargebee/cb-api-documentation/new/dev?filename=samples/cb-app/v2-pcv2/${api}/${snakeCase(endpoint.name)}/${fileName}`
        );
      }
    } else {
      setCodeSample('');
    }
  }, [exportDescription, endpoint]);

  return (
    <Dialog.Root open={isVisible} onOpenChange={setIsVisible}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed inset-0 flex items-center justify-center z-50">
          <Dialog.Title asChild>
            <VisuallyHidden.Root>
              Export Validated Collection
            </VisuallyHidden.Root>
          </Dialog.Title>
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-xl">
            <Form.Root
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Form.Field name="description">
                <Form.Label className="text-lg font-semibold text-gray-800 mb-2 block">
                  Code Sample Description
                </Form.Label>
                <Form.Control asChild>
                  <input
                    ref={inputRef}
                    type="text"
                    value={exportDescription}
                    onChange={(e) => setExportDescription(e.target.value)}
                    placeholder="Enter a description for the code sample"
                    className="w-full border border-gray-300 rounded-md p-2 mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </Form.Control>
                <Form.Message
                  match="valueMissing"
                  className="text-red-500 text-sm mt-1"
                >
                  Description is required.
                </Form.Message>
              </Form.Field>

              <Form.Field name="postman_collection" className="mt-4">
                <Form.Label className="flex text-lg font-semibold text-gray-800 mb-2 block">
                  Postman Collection
                  {codeSample && (
                    <CopyButton content={codeSample} className="text-black" />
                  )}
                </Form.Label>
                <div className="flex items-start gap-2">
                  <textarea
                    value={
                      codeSample || 'Add description to generate code sample'
                    }
                    readOnly
                    className="flex-1 border border-gray-300 rounded-md p-2 mb-1 bg-gray-100 focus:outline-none"
                    rows={4}
                  />
                </div>
              </Form.Field>
              {codeSample && (
                <div className="overflow-y-auto flex-1 flex justify-center items-center text-gray-400 italic">
                  Copy the generated collection and go to github
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    onClick={() => {
                      setExportDescription('');
                      setCodeSample('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 s"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                {codeSample && (
                  <div className="flex justify-end gap-2">
                    <a
                      href={githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-4 py-2 rounded-md ${
                        codeSample
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-300 text-gray-800 cursor-not-allowed pointer-events-none'
                      }`}
                    >
                      Go to GitHub
                    </a>
                  </div>
                )}
              </div>
            </Form.Root>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExportDescriptionCard;
